import { z } from 'zod'
import { DateStringSchema } from './booking'
import { normaliseTags } from './inventory'
import { PERMISSIONS } from './constants'

// The club's general documents: board minutes, meeting papers, rules, forms,
// bulletins (club.documents, issue #192). Keep it in step with V1380-V1410 in
// db/flyway/schema/sql. Aircraft documents are a different thing
// (aircraftDocuments.ts) and stay with the aircraft.
//
// The rules that both the API and the browser apply -- who may see which
// document, and when one may be deleted -- are pure and live here.

// ---------------------------------------------------------------------------
// Visibility

// 'members' is every member; 'board' is the committee only (board minutes).
export const DOCUMENT_VISIBILITIES = ['members', 'board'] as const
export type DocumentVisibility = (typeof DOCUMENT_VISIBILITIES)[number]
export const DocumentVisibilitySchema = z.enum(DOCUMENT_VISIBILITIES)

// The visibilities this holder of `permissions` may see. A board document
// needs the board permission of its own: managing documents does not open the
// board's papers. The API puts this list in the query's WHERE clause, so a
// document the caller may not see never reaches a list, a search, a count or
// a tag suggestion.
export function visibleVisibilities(permissions: Iterable<string>): DocumentVisibility[] {
  const held = new Set(permissions)
  const visible: DocumentVisibility[] = []
  if (held.has(PERMISSIONS.documentRead)) visible.push('members')
  if (held.has(PERMISSIONS.documentReadBoard)) visible.push('board')
  return visible
}

// ---------------------------------------------------------------------------
// Archiving to the jupi-docs repository

// Where a stored file is on its way to the archive. Only 'archived' is
// verified: the file is at its path on the default branch and its git blob
// SHA matches the stored bytes.
export const DOCUMENT_ARCHIVE_STATUSES = ['pending', 'sending', 'pr_open', 'archived', 'failed', 'skipped'] as const
export type DocumentArchiveStatus = (typeof DOCUMENT_ARCHIVE_STATUSES)[number]

// A document, or its R2 object, may be deleted only once it is verifiably in
// the archive. Anything else could be the only copy.
export function isDeletableArchiveStatus(status: DocumentArchiveStatus | null | undefined): boolean {
  return status === 'archived'
}

// ---------------------------------------------------------------------------
// Shapes the API returns

// What a member needs to filter by: the category and how many documents they
// can see in it. The archive folder and the archived flag are for managers.
export interface DocumentCategory {
  categoryKey: string
  nameFi: string
  nameSv: string | null
  nameEn: string | null
  defaultVisibility: DocumentVisibility
  sortOrder: number
  documentCount: number
}

export interface ManagedDocumentCategory extends DocumentCategory {
  archiveFolder: string
  archivedAt: string | null
}

export interface DocumentTag {
  tag: string
  count: number
}

export interface DocumentListResponse {
  documents: ClubDocument[]
  total: number
  page: number
  pageSize: number
}

export interface DocumentMetaResponse {
  categories: DocumentCategory[] | ManagedDocumentCategory[]
  tags: DocumentTag[]
  // The years the visible documents are dated in, newest first, for the year filter.
  years: number[]
}

export interface DocumentArchiveState {
  status: DocumentArchiveStatus
  prUrl: string | null
  archivedAt: string | null
  lastError: string | null
}

export interface ClubDocument {
  documentId: string
  title: string
  description: string
  categoryKey: string
  documentDate: string | null
  tags: string[]
  visibility: DocumentVisibility
  filename: string
  contentType: string
  sizeBytes: number
  uploadedAt: string
  unpublishedAt: string | null
  // Shown to those who manage documents, not to members.
  archive: DocumentArchiveState | null
}

// ---------------------------------------------------------------------------
// Request contracts

const text = (max: number) => z.string().trim().max(max)

// YYYY-MM-DD that is a day on the calendar. The plain format check lets
// 2026-02-30 through, and Postgres then answers with an error instead of the
// 400 the member should get.
const CalendarDateSchema = DateStringSchema.refine((d) => {
  const t = new Date(`${d}T00:00:00Z`)
  return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === d
}, 'Not a calendar date')

export const DOCUMENT_MAX_BYTES = 30 * 1024 * 1024

const CategoryKeySchema = z.string().trim().regex(/^[a-z][a-z0-9_]{1,39}$/, 'Invalid category')

// The metadata an uploader fills in. `visibility` is optional: the category's
// default applies when it is left out.
export const DocumentMetadataSchema = z.object({
  title: text(200).min(1),
  description: text(2000).default(''),
  categoryKey: CategoryKeySchema,
  documentDate: CalendarDateSchema.nullish().transform((v) => v ?? null),
  tags: z.array(z.string()).max(50).default([]).transform(normaliseTags),
  visibility: DocumentVisibilitySchema.optional(),
})
export type DocumentMetadataInput = z.input<typeof DocumentMetadataSchema>

export const UpdateDocumentSchema = z
  .object({
    title: text(200).min(1),
    description: text(2000),
    categoryKey: CategoryKeySchema,
    documentDate: CalendarDateSchema.nullable(),
    tags: z.array(z.string()).max(50).transform(normaliseTags),
    visibility: DocumentVisibilitySchema,
    unpublished: z.boolean(),
  })
  .partial()

export const DOCUMENT_PAGE_SIZE = 50

export const DocumentListQuerySchema = z.object({
  // Free text over title, description, tags and file name.
  q: z.string().trim().max(100).optional(),
  category: CategoryKeySchema.optional(),
  tag: z.string().trim().max(60).optional(),
  // The date OF the document, inclusive.
  from: CalendarDateSchema.optional(),
  to: CalendarDateSchema.optional(),
  // Managers only: also list hidden documents, or only one visibility.
  includeUnpublished: z.enum(['true', 'false']).optional(),
  visibility: DocumentVisibilitySchema.optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(DOCUMENT_PAGE_SIZE),
})
export type DocumentListQuery = z.infer<typeof DocumentListQuerySchema>
